/**
 * Cross-component bridge for "open a Window" from anywhere in the
 * React tree.
 *
 * Problem this solves:
 *  - Deep components (Monero activity log row buttons, tx inspector
 *    deep links, the help app's "Open <feature>" buttons, …) need
 *    to open a top-level WINBIT32 window, but don't have a direct
 *    handle to the `WindowManager`'s `openWindowByProgName`.
 *  - The historical pattern was to set `window.location.hash =
 *    '#prog.exe~<base64>'`, which only routes on initial mount —
 *    subsequent hash changes were silently dropped because the
 *    `hashchange` listener was disabled. Result: every Monero
 *    activity-log click looked like a no-op.
 *
 * The bridge dispatches a single CustomEvent on `window`; the
 * desktop-level `WindowManager` listens for it and calls
 * `openWindowByProgName(progName, metadata)`.
 *
 * The helpers also export the event name as a constant so test
 * doubles can mount the same listener without duplicating the
 * magic string.
 */

/**
 * Event name used for both the global dispatch and the desktop-side
 * listener. Kept stable so future modules don't have to import the
 * helper just to mirror the string.
 */
export const WINBIT32_OPEN_WINDOW_EVENT = 'winbit32:open-window';

/** Detail payload carried by the open-window event. */
export interface OpenWindowEventDetail {
	/**
	 * The `progName` registered in `programs.js`
	 * (e.g. `'monerotxi.exe'`, `'zcashwzrd.exe'`).
	 */
	readonly progName: string;
	/**
	 * Optional metadata forwarded to the opening Window — mirrors
	 * what `WindowManager` already does when parsing a
	 * `progname~base64(metadata)` URL hash on initial load.
	 */
	readonly metadata?: Record<string, unknown>;
}

/**
 * Fire a request to open a top-level WINBIT32 window. Safe to call
 * from anywhere (SSR-safe: the function is a no-op when `window`
 * isn't defined). The dispatch is synchronous, so the caller may
 * read `document.activeElement` etc. immediately after — but the
 * desktop-side listener decides when (and whether) to actually open
 * the window, so the call site should not depend on synchronous
 * mount.
 */
export function requestOpenWindow(
	progName: string,
	metadata?: Record<string, unknown>,
): void {
	if (typeof window === 'undefined' || !progName) return;
	const detail: OpenWindowEventDetail = metadata
		? { progName, metadata }
		: { progName };
	const event = new CustomEvent<OpenWindowEventDetail>(
		WINBIT32_OPEN_WINDOW_EVENT,
		{ detail },
	);
	window.dispatchEvent(event);
}
